'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { Provider } from '@supabase/supabase-js';

import {
  LastAuthMethodHint,
  OauthProviders,
  OtpSignInContainer,
} from '@kit/auth/sign-in';
import { getSafeRedirectPath, isBrowser } from '@kit/shared/utils';
import { If } from '@kit/ui/if';
import { Separator } from '@kit/ui/separator';
import { Trans } from '@kit/ui/trans';

import { MeteorEmailAuthForm } from './meteor-email-auth-form';

/**
 * Custom sign-in methods container with Meteor password fallback
 *
 * This replaces the standard SignInMethodsContainer from @kit/auth/sign-in
 * to support users who still have Meteor passwords.
 */
export function MeteorSignInMethodsContainer(props: {
  paths: {
    callback: string;
    joinTeam: string;
    returnPath: string;
  };

  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    oAuth: Provider[];
  };

  defaultValues?: {
    email: string;
    password: string;
  };
}) {
  const router = useRouter();

  // The magic-link email lands on the callback; carry the safe return path as
  // `next` so a signed-out recipient of a deep link (e.g. a WhatsApp action)
  // returns to that page instead of /home.
  const returnPath = getSafeRedirectPath(props.paths.returnPath, '/home');
  const callbackUrl = isBrowser()
    ? new URL(props.paths.callback, window.location.origin)
    : null;
  callbackUrl?.searchParams.set('next', returnPath);
  const redirectUrl = callbackUrl?.toString() ?? '';

  const onSignIn = useCallback(() => {
    router.replace(returnPath);
  }, [returnPath, router]);

  return (
    <>
      <LastAuthMethodHint />

      {/* One email input shared by both "Sign in with Email" (email + password)
          and "Send me a magic link" (email only, rendered above the providers). */}
      <If condition={props.providers.password}>
        <MeteorEmailAuthForm
          onSignIn={onSignIn}
          redirectUrl={redirectUrl}
          showMagicLink={props.providers.magicLink}
          oauthPaths={{
            callback: props.paths.callback,
            returnPath: props.paths.returnPath,
          }}
        />
      </If>

      <If condition={props.providers.otp}>
        <OtpSignInContainer shouldCreateUser={false} />
      </If>

      <If condition={props.providers.oAuth.length}>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>

          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              <Trans i18nKey="auth:orContinueWith" />
            </span>
          </div>
        </div>

        <OauthProviders
          enabledProviders={props.providers.oAuth}
          shouldCreateUser={false}
          paths={{
            callback: props.paths.callback,
            returnPath: props.paths.returnPath,
          }}
        />
      </If>
    </>
  );
}
